import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

export const MANUAL_SEARCH_PHRASE = 'manual search';
export const FREQUENT_CODES_PHRASE = 'frequently used';

export const isManualSearchGroup = (codeSuggest) => codeSuggest?.phrase === MANUAL_SEARCH_PHRASE;
export const isFrequentCodesGroup = (codeSuggest) => codeSuggest?.phrase === FREQUENT_CODES_PHRASE;

export const getCodeSuggestGroupLabel = (codeSuggest) => {
  if (isManualSearchGroup(codeSuggest)) return 'Manual search';
  if (isFrequentCodesGroup(codeSuggest)) return 'Frequently used';
  return 'Suggestion';
};

export const getCodeSuggestCodeIds = (codeSuggest) =>
  (codeSuggest?.suggestions || [])
    .map((suggestion) => suggestion.codeId)
    .filter(Boolean);

const getPreferredTerm = (existingCode) => existingCode?.code?.terms?.[0];

export const buildExistingCodeById = (existingPatientCodes) => {
  const byId = new Map();
  const addCodes = (codes, category) => {
    codes?.forEach((existingCode) => {
      const term = getPreferredTerm(existingCode);
      if (existingCode.codeId && term && !byId.has(existingCode.codeId)) {
        byId.set(existingCode.codeId, {
          codeId: existingCode.codeId,
          term,
          category,
          isSelected: !!existingCode.isSelected,
        });
      }
    });
  };
  addCodes(existingPatientCodes?.activeCodes, 'Active');
  addCodes(existingPatientCodes?.significantPastCodes, 'Past');
  return byId;
};

export const resolveOverlap = (overlap, codesById) => {
  if (!overlap?.existingCodeIds?.length) {
    return null;
  }
  const matches = overlap.existingCodeIds
    .map((codeId) => codesById.get(codeId))
    .filter(Boolean);
  if (!matches.length) {
    return null;
  }
  return { reason: overlap.reason, matches };
};

const OverlapNotifierCard = ({ overlap, onSelect }) => {
  const active = overlap.matches.filter((match) => match.category === 'Active');
  const past = overlap.matches.filter((match) => match.category === 'Past');

  const renderPartition = (category, matches) => {
    if (!matches.length) {
      return null;
    }
    return (
      <div className="overlap-notifier-partition">
        <span className={`search-result-category ${category.toLowerCase()}`}>
          {category}
        </span>
        {matches.map((match) => {
          const checkClass = `code-checkbox${match.isSelected ? ' is-selected' : ''}`;
          const label = `${match.isSelected ? 'Deselect' : 'Select'} ${match.term}`;
          const check = <span className={checkClass} aria-hidden="true" />;
          return onSelect ? (
            <button
              key={match.codeId}
              type="button"
              className="overlap-notifier-term"
              onClick={() => onSelect(match.codeId, !match.isSelected)}
              aria-pressed={match.isSelected}
              aria-label={label}
            >
              <span className="overlap-notifier-term-label">{match.term}</span>
              {check}
            </button>
          ) : (
            <div key={match.codeId} className="overlap-notifier-term">
              <span className="overlap-notifier-term-label">{match.term}</span>
              {check}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className="overlap-notifier-card">
      <div className="overlap-notifier-label">Existing codes</div>
      {renderPartition('Active', active)}
      {renderPartition('Past', past)}
    </div>
  );
};

export const PotentialPreExistingSectionHeader = ({ canEdit, onDelete }) => (
  <div className="code-suggest-group-header">
    <h4 className="code-suggest-group-heading overlap-notifier">
      <FontAwesomeIcon icon={faTriangleExclamation} />
      Potential pre-existing
    </h4>
    {canEdit && onDelete && (
      <button
        type="button"
        className="code-delete-button code-suggest-group-delete-button"
        onClick={onDelete}
        title="Remove all potentially pre-existing codes"
        aria-label="Remove all potentially pre-existing codes"
      >
        <FontAwesomeIcon icon={faTimes} />
      </button>
    )}
  </div>
);

// Header for a CodeSuggest group with optional delete-all control
export const CodeSuggestGroupHeader = React.memo(({ codeSuggest, canEdit, onDeleteGroup, overlap, onExistingCodeSelect }) => {
  const codeIds = getCodeSuggestCodeIds(codeSuggest);
  const showDelete = canEdit && onDeleteGroup && codeIds.length > 0;
  const label = getCodeSuggestGroupLabel(codeSuggest);

  return (
    <>
      <div className="code-suggest-group-header">
        <h4 className="code-suggest-group-heading">{label}</h4>
        {showDelete && (
          <button
            type="button"
            className="code-delete-button code-suggest-group-delete-button"
            onClick={() => onDeleteGroup(codeIds)}
            title="Remove all codes in this suggestion"
            aria-label="Remove all codes in this suggestion"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        )}
      </div>
      {overlap && (
        <div className="overlap-hover-region">
          {overlap.reason ? (
            <div className="code-suggest-overlap-callout">
              {overlap.reason}
              <OverlapNotifierCard overlap={overlap} onSelect={onExistingCodeSelect} />
            </div>
          ) : (
            <OverlapNotifierCard overlap={overlap} onSelect={onExistingCodeSelect} />
          )}
        </div>
      )}
    </>
  );
});
